import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { SingleValue, MultiValue } from "react-select";
import { AppDispatch, AppState } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { Formik } from "formik";
import { UserRole } from "../../../redux/features/user-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import paths from "../../../navigation/paths";
import { isSelectSingleValue } from "../../../core/utils";
import { format } from "date-fns";
import {
  getCreateCandidateState,
  CandidateCreateRequest,
  createCandidateStateAction,
  clearCreateCandidateState,
  CandidateChannel,
  CandidateStatus,
  EnglishLevel,
} from "../../../redux/features/candidate-slice";
import {
  getAllJDsState,
  allJDsStateAction,
  JDResponse,
} from "../../../redux/features/jd-slice"; // Import JD-related Redux

export default function AddCandidatePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const createCandidateState = useSelector(getCreateCandidateState);
  const allJDsState = useSelector(getAllJDsState); // Get JD state
  const errorState = useSelector(getErrorState);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Fetch all JDs when component mounts
  useEffect(() => {
    dispatch(allJDsStateAction({})); // Fetch all JDs without filters
  }, [dispatch]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const submitCreateCandidate = async (values: CandidateCreateRequest) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("email", values.email);
    formData.append("phone", values.phone);
    if (values.socialLink) formData.append("socialLink", values.socialLink);
    formData.append("channel", values.channel);
    formData.append("status", values.status);
    if (values.currentSalary)
      formData.append("currentSalary", values.currentSalary.toString());
    if (values.expectedSalary)
      formData.append("expectedSalary", values.expectedSalary.toString());
    formData.append("jdId", values.jdId);
    if (values.message) formData.append("message", values.message);
    if (values.certificate) formData.append("certificate", values.certificate);
    formData.append("englishLevel", values.englishLevel);
    if (values.applyDate)
      formData.append("applyDate", values.applyDate.toISOString());
    if (selectedFile) {
      formData.append("cvFile", selectedFile);
    }

    await dispatch(createCandidateStateAction({ request: formData }));
  };

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: paths.CANDIDATES, path: paths.CANDIDATES, title: "Candidates" },
        { id: "addCandidate", path: null, title: "Add Candidate" },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (createCandidateState.data && createCandidateState.data.success) {
      handleSuccessToast(
        createCandidateState.data.message,
        clearCreateCandidateState
      );
      setTimeout(() => {
        navigate(paths.CANDIDATES, { replace: true });
      }, 2000);
    }
  }, [createCandidateState, handleSuccessToast, navigate]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
      console.log(errorState);
    }
  }, [errorState, navigate]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      toast("Please select a valid PDF file", { type: "error" });
      setSelectedFile(null);
    }
  };

  // Map JDs to select options
  const jdOptions: OptionValue[] = allJDsState.data.map((jd: JDResponse) => ({
    value: jd.id,
    label: jd.title,
  }));

  return (
    <PermissionGate categories={[]} roles={[UserRole.Admin, UserRole.Contributor]}>
      <Helmet title="Add Candidate" />
      <Formik
        initialValues={
          {
            name: "",
            email: "",
            phone: "",
            socialLink: "",
            channel: CandidateChannel.Other,
            status: CandidateStatus.Received,
            currentSalary: 0,
            expectedSalary: 0,
            jdId: "",
            message: "",
            certificate: "",
            englishLevel: EnglishLevel.Beginner,
            applyDate: new Date(),
          } as CandidateCreateRequest
        }
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required").max(100),
          email: Yup.string()
            .email("Invalid email")
            .required("Email is required"),
          phone: Yup.string().required("Phone is required").max(15),
          socialLink: Yup.string().url("Invalid URL").optional(),
          channel: Yup.string().required("Channel is required"),
          status: Yup.string().required("Status is required"),
          currentSalary: Yup.number()
            .min(0, "Current salary cannot be negative")
            .optional(),
          expectedSalary: Yup.number()
            .min(0, "Expected salary cannot be negative")
            .optional(),
          jdId: Yup.string().required("Job description is required"),
          message: Yup.string().optional(),
          certificate: Yup.string().optional(),
          englishLevel: Yup.string().required("English level is required"),
          applyDate: Yup.date().required("Apply date is required"),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          await submitCreateCandidate(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          dirty,
        }) => (
          <form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="h-full overflow-y-auto"
            encType="multipart/form-data"
          >
            <div
              className="h-full grid grid-cols-1 sm:grid-cols-2 gap-8 overflow-y-scroll"
              style={{
                height: "calc(100% - 56px)",
              }}
            >
              <div className="space-y-4">
                <Input
                  label="Name *"
                  type="text"
                  id="name"
                  value={values.name}
                  onChange={handleChange}
                  errorText={errors.name}
                />
                <Input
                  label="Email *"
                  type="email"
                  id="email"
                  value={values.email}
                  onChange={handleChange}
                  errorText={errors.email}
                />
                <Input
                  label="Phone *"
                  type="text"
                  id="phone"
                  value={values.phone}
                  onChange={handleChange}
                  errorText={errors.phone}
                />
                <Input
                  label="Social Link"
                  type="text"
                  id="socialLink"
                  value={values.socialLink}
                  onChange={handleChange}
                  errorText={errors.socialLink}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">CV File</label>
                  <input
                    type="file"
                    id="cvFile"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="border border-divider rounded-lg p-2"
                  />
                  {selectedFile && (
                    <p className="text-sm text-text-title">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Channel *</label>
                  <StyledSelect
                    options={Object.values(CandidateChannel).map((channel) => ({
                      value: channel,
                      label: channel.charAt(0).toUpperCase() + channel.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.channel,
                      label:
                        values.channel.charAt(0).toUpperCase() +
                        values.channel.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          channel: value.value as CandidateChannel,
                        });
                      }
                    }}
                  />
                  {errors.channel && (
                    <p className="text-error-main text-sm">{errors.channel}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Status *</label>
                  <StyledSelect
                    options={Object.values(CandidateStatus).map((status) => ({
                      value: status,
                      label: status.charAt(0).toUpperCase() + status.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.status,
                      label:
                        values.status.charAt(0).toUpperCase() +
                        values.status.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          status: value.value as CandidateStatus,
                        });
                      }
                    }}
                  />
                  {errors.status && (
                    <p className="text-error-main text-sm">{errors.status}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">
                    English Level *
                  </label>
                  <StyledSelect
                    options={Object.values(EnglishLevel).map((level) => ({
                      value: level,
                      label: level.charAt(0).toUpperCase() + level.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.englishLevel,
                      label:
                        values.englishLevel.charAt(0).toUpperCase() +
                        values.englishLevel.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          englishLevel: value.value as EnglishLevel,
                        });
                      }
                    }}
                  />
                  {errors.englishLevel && (
                    <p className="text-error-main text-sm">
                      {errors.englishLevel}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <Input
                  label="Current Salary"
                  type="text"
                  id="currentSalary"
                  value={
                    values.currentSalary
                      ? Number(values.currentSalary).toLocaleString("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : ""
                  }
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, "");
                    handleChange({
                      target: {
                        id: "currentSalary",
                        value: rawValue ? parseInt(rawValue, 10) : "",
                      },
                    });
                  }}
                  errorText={errors.currentSalary}
                />
                <Input
                  label="Expected Salary"
                  type="text"
                  id="expectedSalary"
                  value={
                    values.expectedSalary
                      ? Number(values.expectedSalary).toLocaleString("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })
                      : ""
                  }
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/,/g, "");
                    handleChange({
                      target: {
                        id: "expectedSalary",
                        value: rawValue ? parseInt(rawValue, 10) : "",
                      },
                    });
                  }}
                  errorText={errors.expectedSalary}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">
                    Job Description *
                  </label>
                  <StyledSelect
                    options={jdOptions} // Use fetched JD options
                    isClearable={false}
                    menuPosition="fixed"
                    loading={allJDsState.loading} // Show loading state
                    value={
                      jdOptions.find(
                        (option) => option.value === values.jdId
                      ) || undefined
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          jdId: value.value as string,
                        });
                      }
                    }}
                  />
                  {errors.jdId && (
                    <p className="text-error-main text-sm">{errors.jdId}</p>
                  )}
                </div>
                <Input
                  label="Message"
                  type="text"
                  inputType="textarea"
                  id="message"
                  value={values.message}
                  onChange={handleChange}
                  errorText={errors.message}
                />
                <Input
                  label="Certificate"
                  type="text"
                  id="certificate"
                  value={values.certificate}
                  onChange={handleChange}
                  errorText={errors.certificate}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">
                    Apply Date *
                  </label>
                  <input
                    type="date"
                    id="applyDate"
                    value={
                      values.applyDate
                        ? format(values.applyDate, "yyyy-MM-dd")
                        : ""
                    }
                    onChange={(e) => {
                      setValues({
                        ...values,
                        applyDate: e.target.value
                          ? new Date(e.target.value)
                          : undefined,
                      });
                    }}
                    className="border border-divider rounded-lg p-2"
                  />
                  {errors.applyDate && (
                    <p className="text-error-main text-sm">
                      {errors.applyDate as string}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-4">
              <Button
                label="Submit"
                type="submit"
                loading={createCandidateState.loading}
                disabled={
                  !dirty || isSubmitting || createCandidateState.loading
                }
              />
            </div>
          </form>
        )}
      </Formik>
      <ToastComponent id="addCandidate" />
    </PermissionGate>
  );
}
